<template>
  <div
    class="segment-image-panel"
    v-if="shouldDisplayImage"
    :class="{ 'is-fullscreen': isFullscreen, 'is-playing': isPlaying, 'is-recording': isRecording }"
    tabindex="0"
  >
    <!-- Recording countdown overlay -->
    <recording-countdown
      ref="countdown"
      :duration="countdownDuration"
      @complete="onCountdownComplete"
      @cancelled="onCountdownCancelled"
    />

    <!-- Recording Error Notification -->
    <transition name="fade">
      <div v-if="recordingError" class="recording-error-notification">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>{{ recordingError }}</span>
        <button @click="recordingError = null" class="error-close-btn">&times;</button>
      </div>
    </transition>

    <div class="image-display" @click="openImageUrl(currentImage.url)">
      <div class="image-container animated">
        <div
          class="image-animation-container"
          :class="[currentAnimation, noRepeatAnimation ? 'no-repeat' : '']"
        >
          <img
            :src="currentImage.image || currentImage.thumbnail"
            :alt="currentImage.title"
            @error="handleImageError"
            class="document-image"
          />
        </div>

        <!-- Add Live Transcript component -->
        <live-transcript
          v-if="isPlaying"
          :message-id="messageId"
          :voice-speed="voiceSpeed"
          :text="activeSegmentContent"
          :speaker="activeSegmentSpeaker"
          :speaker-image="speakerImage"
          :visible="isPlaying"
        />
      </div>

      <div class="image-footer">
        <div class="image-source">
          Source: <a :href="currentImage.url" target="_blank" rel="noopener noreferrer">{{ formatUrl(currentImage.url) }}</a>
        </div>
      </div>

      <!-- UI Controls Layer - Updated to support recording mode -->
      <div class="ui-controls-layer" :class="{ 'hidden': (isPlaying && !showControls && recordingMode) || forceHideAllUI, 'recording-hidden': isRecording }">
        <!-- Fullscreen toggle -->
        <button
          v-if="!isRecording"
          @click.stop="toggleFullscreen"
          class="fullscreen-toggle"
          :class="{ 'recording-mode': recordingMode }"
          aria-label="Toggle fullscreen"
        >
          <svg v-if="!isFullscreen" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
            <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
            <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
            <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 3v3a2 2 0 0 1-2 2H3"></path>
            <path d="M21 8h-3a2 2 0 0 1-2-2V3"></path>
            <path d="M3 16h3a2 2 0 0 1 2 2v3"></path>
            <path d="M16 21v-3a2 2 0 0 1 2-2h3"></path>
          </svg>
        </button>

        <!-- Large play button and record button (only visible when paused) -->
        <div v-if="!isPlaying && !isPreparingRecording" class="large-play-button-container">
          <button class="nav-button prev-button" @click.stop="skipPrevious" aria-label="Previous segment">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="19 20 9 12 19 4 19 20"></polygon>
              <line x1="5" y1="19" x2="5" y2="5"></line>
            </svg>
          </button>

          <!-- Play button -->
          <button class="large-play-button" @click.stop="playOnly" aria-label="Play">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          </button>

          <!-- Record button (if recording mode is enabled) -->
          <button v-if="recordingMode" class="large-record-button" @click.stop="recordAndPlay" aria-label="Record and Play">
            <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <circle cx="12" cy="12" r="6" fill="currentColor"></circle>
            </svg>
          </button>

          <button class="nav-button next-button" @click.stop="skipNext" aria-label="Next segment">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 4 15 12 5 20 5 4"></polygon>
              <line x1="19" y1="5" x2="19" y2="19"></line>
            </svg>
          </button>
        </div>

        <!-- Only show animation settings when not playing and not recording -->
        <div v-if="!isPlaying && !isRecording && (isFullscreen || isMobile)" class="recording-settings">
          <!-- Settings toggle button -->
          <button @click.stop="toggleSettings" class="settings-toggle" aria-label="Toggle settings">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
          </button>

          <!-- Settings panel (conditionally shown) -->
          <div v-if="showSettingsPanel" class="settings-panel" @click.stop>
            <!-- Recording mode toggle -->
            <div class="settings-section">
              <label class="settings-label">
                <input type="checkbox" v-model="recordingMode" @change="saveSettings">
                <span>Recording Mode</span>
                <span class="settings-hint">(Enables tab recording with screen capture)</span>
              </label>
            </div>

            <!-- Animation style selector -->
            <div class="settings-section">
              <label>Animation Style:</label>
              <div class="setting-buttons">
                <button
                  v-for="anim in availableAnimations"
                  :key="anim.value"
                  @click.stop="setAnimationStyle(anim.value)"
                  :class="{ active: animationStyle === anim.value }"
                  class="setting-button"
                >
                  {{ anim.label }}
                </button>
              </div>

              <!-- Animation options -->
              <div class="animation-options">
                <label class="settings-checkbox">
                  <input type="checkbox" v-model="holdAnimationAtEnd" @change="saveSettings">
                  <span>Hold at end position</span>
                </label>

                <label class="settings-checkbox">
                  <input type="checkbox" v-model="synchronizeWithSpeaking" @change="saveSettings">
                  <span>Match speaking duration</span>
                </label>

                <label v-if="!holdAnimationAtEnd && !synchronizeWithSpeaking" class="settings-checkbox">
                  <input type="checkbox" v-model="noRepeatAnimation" @change="saveSettings">
                  <span>Don't repeat animation</span>
                </label>
              </div>
            </div>

            <!-- Aspect Ratio -->
            <div class="settings-section">
              <label>Aspect Ratio:</label>
              <div class="setting-buttons">
                <button
                  v-for="ratio in availableAspectRatios"
                  :key="ratio.value"
                  @click.stop="setAspectRatio(ratio.value)"
                  :class="{ active: aspectRatio === ratio.value }"
                  class="setting-button"
                >
                  {{ ratio.label }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Keyboard shortcut overlay - Press '?' to toggle -->
      <div v-if="showKeyboardShortcuts" class="keyboard-shortcuts-overlay" @click.stop="showKeyboardShortcuts = false">
        <div class="keyboard-shortcuts-panel" @click.stop>
          <h3>Keyboard Shortcuts</h3>
          <div class="shortcut-list">
            <div class="shortcut-item">
              <span class="key">Space</span>
              <span>Play/Pause</span>
            </div>
            <div class="shortcut-item">
              <span class="key">F</span>
              <span>Toggle Fullscreen</span>
            </div>
            <div class="shortcut-item">
              <span class="key">←</span>
              <span>Previous Segment</span>
            </div>
            <div class="shortcut-item">
              <span class="key">→</span>
              <span>Next Segment</span>
            </div>
            <div class="shortcut-item">
              <span class="key">R</span>
              <span>Toggle Recording Mode</span>
            </div>
            <div class="shortcut-item" v-if="isRecording">
              <span class="key">Q</span>
              <span>Stop Recording</span>
            </div>
            <div class="shortcut-item" v-if="isRecording">
              <span class="key">P</span>
              <span>Pause/Resume Recording</span>
            </div>
            <div class="shortcut-item">
              <span class="key">?</span>
              <span>Show/Hide Shortcuts</span>
            </div>
          </div>
          <button @click.stop="showKeyboardShortcuts = false" class="close-button">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Empty State Placeholder (when no content is available) -->
  <div
    v-else-if="!showDocumentsPanel"
    class="empty-state-placeholder"
    :class="[aspectRatioClass, { 'is-fullscreen': isFullscreen, 'is-playing': isPlaying, 'is-recording': isRecording }]"
    tabindex="0"
  >
    <!-- Recording countdown overlay for empty state -->
    <recording-countdown
      ref="emptyStateCountdown"
      :duration="countdownDuration"
      @complete="onCountdownComplete"
      @cancelled="onCountdownCancelled"
    />

    <div class="placeholder-image-container">
      <div class="image-container animated">
        <div class="image-animation-container" :class="currentAnimation">
          <img
            :src="randomPlaceholderImage"
            alt="Placeholder Image"
            class="placeholder-image document-image"
          />
        </div>

        <!-- Add Live Transcript component to empty state -->
        <live-transcript
          v-if="isPlaying"
          :message-id="messageId"
          :voice-speed="voiceSpeed"
          :text="activeSegmentContent"
          :speaker="activeSegmentSpeaker"
          :speaker-image="speakerImage"
          :visible="isPlaying"
        />
      </div>
    </div>

    <!-- UI Controls for placeholder with recording support -->
    <div class="ui-controls-layer" :class="{ 'hidden': (isPlaying && !showControls && recordingMode) || forceHideAllUI, 'recording-hidden': isRecording }">
      <!-- Fullscreen toggle for placeholder -->
      <button
        v-if="!isRecording"
        @click.stop="toggleFullscreen"
        class="fullscreen-toggle"
        :class="{ 'recording-mode': recordingMode }"
        aria-label="Toggle fullscreen"
      >
        <svg v-if="!isFullscreen" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
          <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
          <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
          <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8 3v3a2 2 0 0 1-2 2H3"></path>
          <path d="M21 8h-3a2 2 0 0 1-2-2V3"></path>
          <path d="M3 16h3a2 2 0 0 1 2 2v3"></path>
          <path d="M16 21v-3a2 2 0 0 1 2-2h3"></path>
        </svg>
      </button>

      <!-- Large play button (only visible when paused) -->
      <div v-if="!isPlaying && !isPreparingRecording" class="large-play-button-container">
        <button class="nav-button prev-button" @click.stop="skipPrevious" aria-label="Previous segment">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="19 20 9 12 19 4 19 20"></polygon>
            <line x1="5" y1="19" x2="5" y2="5"></line>
          </svg>
        </button>

        <!-- Play button -->
        <button class="large-play-button" @click.stop="playOnly" aria-label="Play">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="5 3 19 12 5 21 5 3"></polygon>
          </svg>
        </button>

        <!-- Record button -->
        <button v-if="recordingMode" class="large-record-button" @click.stop="recordAndPlay" aria-label="Record and Play">
          <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <circle cx="12" cy="12" r="6" fill="currentColor"></circle>
          </svg>
        </button>

        <button class="nav-button next-button" @click.stop="skipNext" aria-label="Next segment">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="5 4 15 12 5 20 5 4"></polygon>
            <line x1="19" y1="5" x2="19" y2="19"></line>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LiveTranscript from '../components/LiveTranscript.vue';
import tabRecordingMixin from '../mixins/tabRecordingMixin';
import RecordingCountdown from '../components/recording/RecordingCountdown.vue';

export default {
  name: 'FullscreenImagePanel',
  components: {
    LiveTranscript,
    RecordingCountdown
  },
  mixins: [tabRecordingMixin],
  props: {
    messageId: {
      required: true,
    },
    voiceSpeed: {
      type: Number,
      default: 1
    },
    speakerImage: {
      type: String,
      default: '/static/Gemini_Generated_Image_uobiifuobiifuobi.jpeg'
    },
    searchResults: {
      type: Array,
      default: () => []
    },
    activeSegment: {
      type: Number,
      default: 0
    },
    totalSegments: {
      type: Number,
      default: 0
    },
    showCitationPanel: {
      type: Boolean,
      default: false
    },
    showDocumentsPanel: {
      type: Boolean,
      default: false
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    activeSegmentContent: {
      type: String,
      default: ''
    },
    activeSegmentSpeaker: {
      type: String,
      default: ''
    },
    transcriptBackgroundColor: {
      type: String,
      default: 'rgba(0, 0, 0, 0.6)'
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // UI Controls
      showControls: true,
      controlsTimer: null,
      showSettingsPanel: false,
      recordingMode: true,
      showKeyboardShortcuts: false,

      // Fullscreen state
      isFullscreen: false,

      // Animation options
      availableAnimations: [
        { value: 'random', label: 'Random' },
        { value: 'zoom-pan', label: 'Zoom & Pan' },
        { value: 'ken-burns', label: 'Ken Burns' },
        { value: 'fade-scale', label: 'Fade & Scale' },
        { value: 'gentle-float', label: 'Gentle Float' },
        { value: 'smooth-slide', label: 'Smooth Slide' },
        { value: 'hollywood-dramatic', label: 'Hollywood Dramatic' }

      ],
      availableAspectRatios: [
        { value: '16:9', label: '16:9' },
        { value: '4:3', label: '4:3' },
        { value: '1:1', label: '1:1' },
        { value: '9:16', label: '9:16' }
      ],
      animations: [
        'zoom-pan-effect',
        'ken-burns-effect',
        'fade-and-scale',
        'gentle-float',
        'smooth-slide',
        'hollywood-dramatic',
      ],
      animationIndex: 0,
      animationStyle: 'zoom-pan',
      aspectRatio: '16:9',
      animationTimer: null,
      currentAnimation: 'zoom-pan-effect',
      noRepeatAnimation: false,
      holdAnimationAtEnd: true,
      synchronizeWithSpeaking: true,
      segmentInProgress: false,

      // For handling failed images
      failedImageUrls: new Set(),

      // Placeholder images
      placeholderImages: [
        "/static/Gemini_Generated_Image_afdde4afdde4afdd.jpeg",
        "/static/Gemini_Generated_Image_arxot0arxot0arxo.jpeg",
        "/static/Gemini_Generated_Image_jmfhwfjmfhwfjmfh.jpeg",
        "/static/Gemini_Generated_Image_ttj4r8ttj4r8ttj4.jpeg",
        "/static/Gemini_Generated_Image_28fjog28fjog28fj.jpeg",
        "/static/Gemini_Generated_Image_z58uvpz58uvpz58u.jpeg",
        "/static/Gemini_Generated_Image_2p68i32p68i32p68.jpeg",
        "/static/Gemini_Generated_Image_lmzdtllmzdtllmzd.jpeg",
        "/static/Gemini_Generated_Image_nvx3l3nvx3l3nvx3.jpeg",
        "/static/Gemini_Generated_Image_hrlvv0hrlvv0hrlv.jpeg",
        "/static/Gemini_Generated_Image_wuxx6lwuxx6lwuxx.jpeg",
        "/static/Gemini_Generated_Image_8a43t08a43t08a43.jpeg",
        "/static/Gemini_Generated_Image_cq7nnqcq7nnqcq7n.jpeg",
        "/static/Gemini_Generated_Image_sekakwsekakwseka.jpeg",
        "/static/Gemini_Generated_Image_zcz3ndzcz3ndzcz3.jpeg",
      ],
      currentPlaceholderImageIndex: Math.floor(Math.random() * 16),
      previousImageState: false,
      isSegmentImageShowing: false,
      lastInteractionTime: Date.now(),

      // Recording specific states
      forceHideAllUI: false,
      isRecordingPaused: false,
      pendingStream: null,
      countdownDuration: 7,
      showCountdown: false
    };
  },
  computed: {
    // Get current aspect ratio class for CSS
    aspectRatioClass() {
      switch (this.aspectRatio) {
        case '16:9': return 'aspect-16-9';
        case '4:3': return 'aspect-4-3';
        case '1:1': return 'aspect-1-1';
        case '9:16': return 'aspect-9-16';
        default: return 'aspect-16-9';
      }
    },

    // Get the current random placeholder image
    randomPlaceholderImage() {
      return this.placeholderImages[this.currentPlaceholderImageIndex];
    },

    // Filter images from search results
    searchResultImages() {
      if (!this.searchResults || this.searchResults.length === 0) return [];

      return this.searchResults.filter(result => {
        if (!result.url) return false;

        const url = result.url.toLowerCase();
        if (url.includes('facebook.com') ||
            url.includes('fbcdn.net') ||
            url.includes('instagram.com')) {
          return false;
        }

        if (result.isImage) return true;

        const imageExtensions = [
          '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp',
          '.ico', '.tiff', '.tif', '.img', '.heic', '.heif', '.raw', '.avif'
        ];

        const hasImageExtension = imageExtensions.some(ext => url.includes(ext));
        if (hasImageExtension) return true;

        const imagePatterns = [
          'image', 'img', 'picture', 'photo', 'thumbnail', 'thumb',
          '/images/', '/pics/', '/photos/', '/avatars/', '/covers/',
          'media.licdn.com/dms/image',
          'profile-images', 'user-images',
          'twimg.com',
          'unsplash.com',
          'pexels.com',
          'imgur.com',
          'pinimg.com',
          'cloudfront.net',
          'cloudinary.com',
          'googleusercontent.com',
          'gravatar.com'
        ];

        return imagePatterns.some(pattern => url.includes(pattern));
      });
    },

    imageSegmentMapping() {
      const images = this.searchResultImages;
      if (!images || images.length === 0) return [];

      const mapping = [];
      const totalSegments = this.totalSegments || 30;

      for (let segment = 0; segment < totalSegments; segment++) {
        const imageIndex = segment % images.length;
        mapping.push({ segmentIndex: segment, imageIndex });
      }

      return mapping;
    },

    currentImage() {
      const mapping = this.imageSegmentMapping.find(m => m.segmentIndex === this.activeSegment);

      if (mapping && this.searchResultImages.length > mapping.imageIndex) {
        return this.searchResultImages[mapping.imageIndex];
      }

      return this.searchResultImages.length > 0 ? this.searchResultImages[0] : null;
    },

    shouldDisplayImage() {
      if (this.showDocumentsPanel) return false;
      if (!this.currentImage) return false;
      return this.imageSegmentMapping.some(mapping => mapping.segmentIndex === this.activeSegment);
    }
  },
  watch: {
    isPlaying(newVal) {
      if (newVal) {
        // Started playing
        this.segmentInProgress = true;
        this.restartAnimations();

        // Always hide controls when playing in fullscreen
        if (this.isFullscreen) {
          this.showControls = false;
          this.forceHideAllUI = true; // Add this line
        } else if (this.recordingMode && this.isRecording) {
          this.showControls = false;
        } else {
          this.showControlsTemporarily();
        }
      } else {
        // Stopped playing
        this.segmentInProgress = false;
        this.forceHideAllUI = false; // Add this line

        // Always show controls when stopped, unless recording
        if (!this.isRecording) {
          this.showControls = true;
        }
      }
    },

    // Watch for changes in the image display state
    shouldDisplayImage(newValue, oldValue) {
      if (newValue !== oldValue) {
        // Emit event when the visibility changes
        this.$emit('image-visibility-changed', newValue, this.currentImage);
        this.isSegmentImageShowing = newValue;
      }
      this.previousImageState = newValue;
    },

    // Watch for changes in active segment
    activeSegment() {
      // Check image display state after segment changes
      this.$nextTick(() => {
        if (this.shouldDisplayImage !== this.previousImageState) {
          this.$emit('image-visibility-changed', this.shouldDisplayImage, this.currentImage);
          this.isSegmentImageShowing = this.shouldDisplayImage;
          this.previousImageState = this.shouldDisplayImage;
        }

        // Select a new random placeholder when segment changes
        this.selectRandomPlaceholder();

        // Handle animation style changes
        if (this.animationStyle === 'random' && this.isPlaying) {
          this.changeRandomAnimation();
        }

        // Restart animations
        this.restartAnimations();
      });
    },

    // Watch for active segment content changes
    activeSegmentContent(newContent, oldContent) {
      if (newContent !== oldContent && newContent !== '') {
        // New segment content
        this.segmentInProgress = true;

        // Handle animation style changes
        if (this.animationStyle === 'random' && this.isPlaying) {
          this.changeRandomAnimation();
        }

        // Restart animations
        if (this.synchronizeWithSpeaking && this.isPlaying) {
          this.restartAnimations();
        }
      } else if (newContent === '' && oldContent !== '') {
        this.segmentInProgress = false;
      }
    },

    // Watch recording mode changes
    recordingMode(newVal) {
      // If entering recording mode, hide controls
      if (newVal && this.isPlaying) {
        this.showControls = false;
      }
    }
  },
  mounted() {
    this.loadSettings();
    this.addEventListeners();
    this.startAnimationCycle();

    // Initial emission
    this.$nextTick(() => {
      this.$emit('image-visibility-changed', this.shouldDisplayImage, this.currentImage);
      this.isSegmentImageShowing = this.shouldDisplayImage;
      this.previousImageState = this.shouldDisplayImage;
    });
  },
  beforeDestroy() {
    this.removeEventListeners();

    if (this.controlsTimer) {
      clearTimeout(this.controlsTimer);
    }

    if (this.animationTimer) {
      clearInterval(this.animationTimer);
    }
  },
  methods: {
    // ---- Event Handling ----
    addEventListeners() {
      // Add fullscreen change event listener
      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);

      // Add keyboard event listeners
      window.addEventListener('keydown', this.handleKeyDown);

      // Mouse movement tracking
      if (this.$el) {
        this.$el.addEventListener('mousemove', this.handleMouseMove);
      }
    },

    removeEventListeners() {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      window.removeEventListener('keydown', this.handleKeyDown);

      if (this.$el) {
        this.$el.removeEventListener('mousemove', this.handleMouseMove);
      }
    },

    handleKeyDown(e) {
      // Only handle if this component is focused
      if (document.activeElement !== this.$el && !this.isFullscreen) return;

      // Handle recording-specific keys first
      if (this.isRecording) {
        this.handleRecordingKeyDown(e);
        return;
      }

      switch (e.key) {
        case ' ': // Space - Play/Pause
          e.preventDefault();
          this.togglePlay();
          break;
        case 'f': // F - Fullscreen
        case 'F':
          e.preventDefault();
          this.toggleFullscreen();
          break;
        case 'ArrowLeft': // Left arrow - Previous
          e.preventDefault();
          this.skipPrevious();
          break;
        case 'ArrowRight': // Right arrow - Next
          e.preventDefault();
          this.skipNext();
          break;
        case '?': // ? - Show keyboard shortcuts
          e.preventDefault();
          this.showKeyboardShortcuts = !this.showKeyboardShortcuts;
          break;
        case 'r': // R - Toggle recording mode
        case 'R':
          e.preventDefault();
          this.recordingMode = !this.recordingMode;
          this.saveSettings();
          break;
      }
    },

    handleEscKey() {
      if (this.isRecording) {
        this.stopRecording();
      } else if (this.isFullscreen) {
        this.exitFullscreen();
      }
    },

    handleMouseMove() {
      // Update last interaction time
      this.lastInteractionTime = Date.now();

      // Only show controls on mouse move if not in fullscreen playing mode
      if (!(this.isFullscreen && this.isPlaying)) {
        if (!this.recordingMode || !this.isPlaying || !this.isRecording) {
          this.showControlsTemporarily();
        }
      }
    },

    // ---- UI Control Methods ----
    togglePlay() {
      this.$emit('toggle-play');
    },

    playOnly() {
      // Just play without recording
      this.$emit('toggle-play');
    },

    recordAndPlay() {
      // Start recording and then play
      if (!this.isRecording) {
        this.forceHideAllUI = true;
        this.startRecording();
      }
    },

    skipPrevious() {
      this.$emit('skip-previous');
    },

    skipNext() {
      this.$emit('skip-next');
    },

    toggleSettings() {
      this.showSettingsPanel = !this.showSettingsPanel;
    },

    // Show controls temporarily
    showControlsTemporarily() {
       // If in fullscreen and playing, never show controls
      if (this.isFullscreen && this.isPlaying) {
        this.showControls = false;
        return; // Exit early
      }

      // Clear existing timer
      if (this.controlsTimer) {
        clearTimeout(this.controlsTimer);
      }

      // Show controls
      this.showControls = true;

      // Hide after delay if playing in recording mode and not recording
      if (this.isPlaying && this.recordingMode && !this.isRecording) {
        this.controlsTimer = setTimeout(() => {
          this.showControls = false;
        }, 2500);
      }
    },

    // Start recording with countdown
    async startRecordingWithCountdown() {
      try {
        // First request permissions
        if (!await this.initializeRecording()) {
          return;
        }

        this.isPreparingRecording = true;
        this.recordingError = null;

        // Get display media (tab capture) with enhanced audio
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: this.recordingQuality.video,
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            sampleRate: { ideal: 48000 },
            channelCount: { ideal: 2 }
          },
          preferCurrentTab: true,
        });

        // Check if user selected current tab
        const videoTrack = displayStream.getVideoTracks()[0];
        const settings = videoTrack.getSettings();

        // Only proceed if a browser tab was selected
        if (settings.displaySurface !== 'browser') {
          this.recordingError = 'Please select a browser tab to record';
          displayStream.getTracks().forEach(track => track.stop());
          this.isPreparingRecording = false;
          return;
        }

        // Store the stream for later use
        this.pendingStream = displayStream;

        // Handle stream ended (user stopped sharing)
        displayStream.getVideoTracks()[0].onended = () => {
          this.cancelCountdown();
        };

        // Enter fullscreen first
        if (!this.isFullscreen) {
          await this.enterFullscreen();
        }

        // Wait a moment for fullscreen to settle
        await new Promise(resolve => setTimeout(resolve, 300));

        // Show countdown
        this.showCountdown = true;

        // Start countdown
        this.$nextTick(() => {
          const countdownRef = this.shouldDisplayImage ? 'countdown' : 'emptyStateCountdown';
          if (this.$refs[countdownRef]) {
            this.$refs[countdownRef].start();
          }
        });

      } catch (error) {
        console.error('Error preparing recording:', error);
        this.recordingError = error.message || 'Failed to start recording';
        this.isPreparingRecording = false;
        this.cleanup();
      }
    },

    // Handle countdown complete
    async onCountdownComplete() {
      this.showCountdown = false;

      // Give time for countdown to disappear and DOM to update
      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 300));

      if (!this.pendingStream) {
        this.recordingError = 'Recording stream not available';
        this.isPreparingRecording = false;
        return;
      }

      try {
        // Set up MediaRecorder with the stream
        this.recordingStream = this.pendingStream;
        this.pendingStream = null;

        this.mediaRecorder = new MediaRecorder(this.recordingStream, this.recordingOptions);
        this.recordedChunks = [];

        // Handle data available
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.recordedChunks.push(event.data);
          }
        };

        // Handle recording stopped
        this.mediaRecorder.onstop = () => {
          this.saveRecording();
          this.cleanup();
        };

        // Handle errors
        this.mediaRecorder.onerror = (event) => {
          console.error('MediaRecorder error:', event);
          this.recordingError = 'Recording error occurred';
          this.stopRecording();
        };

        // Start recording
        this.mediaRecorder.start(1000);
        this.isRecording = true;
        this.forceHideAllUI = true;
        this.recordingStartTime = Date.now();
        this.startRecordingTimer();
        this.isPreparingRecording = false;

        // Start playback
        if (!this.isPlaying) {
          this.togglePlay();
        }

        // Hide all UI controls for clean recording
        this.showControls = false;
        this.forceHideAllUI = true;

      } catch (error) {
        console.error('Error starting recording:', error);
        this.recordingError = error.message || 'Failed to start recording';
        this.isPreparingRecording = false;
        this.cleanup();
      }
    },

    // Handle countdown cancelled
    onCountdownCancelled() {
      this.showCountdown = false;
      this.isPreparingRecording = false;

      // Stop the pending stream
      if (this.pendingStream) {
        this.pendingStream.getTracks().forEach(track => track.stop());
        this.pendingStream = null;
      }

      // Exit fullscreen if we entered it
      if (this.isFullscreen) {
        this.exitFullscreen();
      }

      // Show controls again
      this.showControls = true;
    },

    // Cancel countdown (from external trigger)
    cancelCountdown() {
      const countdownRef = this.shouldDisplayImage ? 'countdown' : 'emptyStateCountdown';
      if (this.$refs[countdownRef]) {
        this.$refs[countdownRef].cancelCountdown();
      }
    },

    // Recording pause toggle
    toggleRecordingPause() {
      if (this.mediaRecorder) {
        if (this.mediaRecorder.state === 'recording') {
          this.mediaRecorder.pause();
          this.isRecordingPaused = true;
        } else if (this.mediaRecorder.state === 'paused') {
          this.mediaRecorder.resume();
          this.isRecordingPaused = false;
        }
      }
    },

    // ---- Animation Methods ----
    changeRandomAnimation() {
      let newIndex;
      do {
        newIndex = Math.floor(Math.random() * this.animations.length);
      } while (newIndex === this.animationIndex && this.animations.length > 1);

      this.animationIndex = newIndex;
      this.currentAnimation = this.animations[this.animationIndex];

      const animContainer = this.$el.querySelector('.image-animation-container');
      if (animContainer) {
        animContainer.classList.add('animation-transition');

        this.animations.forEach(anim => {
          animContainer.classList.remove(anim);
        });

        void animContainer.offsetWidth;

        animContainer.classList.add(this.currentAnimation);

        setTimeout(() => {
          animContainer.classList.remove('animation-transition');
        }, 1000);
      }
    },

    restartAnimations() {
      const animContainer = this.$el?.querySelector('.image-animation-container');
      if (animContainer) {
        if (this.segmentInProgress) {
          animContainer.classList.add('animation-transition');
        }

        this.animations.forEach(anim => {
          animContainer.classList.remove(anim);
        });

        void animContainer.offsetWidth;

        animContainer.classList.add(this.currentAnimation);

        if (this.holdAnimationAtEnd || this.synchronizeWithSpeaking) {
          const wordCount = this.activeSegmentContent.split(/\s+/).length;
          const estimatedSeconds = Math.max(10, Math.min(60, (wordCount / 90) * 60));

          animContainer.style.animationDuration = `${estimatedSeconds}s`;
        } else {
          animContainer.style.animationDuration = '';
        }

        setTimeout(() => {
          animContainer.classList.remove('animation-transition');
        }, 1000);
      }
    },

    startAnimationCycle() {
      if (this.animationTimer) {
        clearInterval(this.animationTimer);
        this.animationTimer = null;
      }

      if (this.animationStyle === 'random' && !this.holdAnimationAtEnd && !this.synchronizeWithSpeaking) {
        this.currentAnimation = this.animations[this.animationIndex];

        if (!this.noRepeatAnimation) {
          this.animationTimer = setInterval(() => {
            if (!this.segmentInProgress || !this.synchronizeWithSpeaking) {
              this.changeRandomAnimation();
            }
          }, 15000);
        }
      } else {
        const animMap = {
          'zoom-pan': 'zoom-pan-effect',
          'ken-burns': 'ken-burns-effect',
          'fade-scale': 'fade-and-scale',
          'gentle-float': 'gentle-float',
          'smooth-slide': 'smooth-slide',
          'hollywood-dramatic': 'hollywood-dramatic'
        };
        this.currentAnimation = animMap[this.animationStyle] || 'zoom-pan-effect';
      }

      this.$nextTick(() => {
        this.restartAnimations();
      });
    },

    // ---- Settings Methods ----
    setAnimationStyle(style) {
      this.animationStyle = style;
      this.startAnimationCycle();
      this.saveSettings();

      if (this.isPlaying) {
        this.restartAnimations();
      }
    },

    setAspectRatio(ratio) {
      this.aspectRatio = ratio;
      this.saveSettings();
    },

    saveSettings() {
      localStorage.setItem(`image_panel_settings_${this.messageId}`, JSON.stringify({
        aspectRatio: this.aspectRatio,
        animationStyle: this.animationStyle,
        noRepeatAnimation: this.noRepeatAnimation,
        holdAnimationAtEnd: this.holdAnimationAtEnd,
        synchronizeWithSpeaking: this.synchronizeWithSpeaking,
        recordingMode: this.recordingMode
      }));
    },

    loadSettings() {
      const savedSettings = localStorage.getItem(`image_panel_settings_${this.messageId}`);
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings);
          if (settings.aspectRatio) {
            this.aspectRatio = settings.aspectRatio;
          }
          if (settings.animationStyle) {
            this.animationStyle = settings.animationStyle;
          }
          if (settings.noRepeatAnimation !== undefined) {
            this.noRepeatAnimation = settings.noRepeatAnimation;
          }
          if (settings.holdAnimationAtEnd !== undefined) {
            this.holdAnimationAtEnd = settings.holdAnimationAtEnd;
          }
          if (settings.synchronizeWithSpeaking !== undefined) {
            this.synchronizeWithSpeaking = settings.synchronizeWithSpeaking;
          }
          if (settings.recordingMode !== undefined) {
            this.recordingMode = settings.recordingMode;
          }
        } catch (e) {
          console.error('Error loading saved settings:', e);
        }
      }
    },

    // URL formatting
    formatUrl(url) {
      if (!url) return '';

      try {
        const urlObj = new URL(url);
        return urlObj.hostname.replace(/^www\./, '');
      } catch (e) {
        return url.length > 30 ? url.substring(0, 30) + '...' : url;
      }
    },

    // Select a random placeholder image
    selectRandomPlaceholder() {
      this.currentPlaceholderImageIndex = Math.floor(Math.random() * this.placeholderImages.length);
    },

    // Handle image errors
    handleImageError(event) {
      console.warn('Image load error:', event.target.alt);

      const failedUrl = this.currentImage.url || this.currentImage.image;
      this.failedImageUrls.add(failedUrl);

      const availableImages = this.searchResultImages.filter(img =>
        !this.failedImageUrls.has(img.url || img.image)
      );

      if (availableImages.length > 0) {
        const newImage = availableImages[0];

        event.target.src = newImage.image || newImage.thumbnail;
        event.target.alt = newImage.title;

        this.$emit('image-changed', newImage);
      } else {
        const container = event.target.parentNode;

        this.selectRandomPlaceholder();
        const randomPlaceholder = this.randomPlaceholderImage;

        const fallbackImg = document.createElement('img');
        fallbackImg.src = randomPlaceholder;
        fallbackImg.alt = "Fallback image";
        fallbackImg.className = "fallback-image document-image";

        fallbackImg.style.maxHeight = "500px";
        fallbackImg.style.width = "100%";
        fallbackImg.style.objectFit = "cover";

        event.target.style.display = 'none';

        container.appendChild(fallbackImg);

        const errorMsg = document.createElement('div');
        errorMsg.className = 'image-error-message';
        errorMsg.textContent = 'Image could not be loaded, showing a placeholder image';
        container.appendChild(errorMsg);
      }
    },

    // Check if an image is shown for a specific segment
    isImageShownForSegment(segmentIndex) {
      if (this.showCitationPanel || this.showDocumentsPanel) return false;
      if (!this.searchResultImages.length) return false;

      return this.imageSegmentMapping.some(mapping => mapping.segmentIndex === segmentIndex);
    },

    // Open image URL in new tab
    openImageUrl(url) {
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },

    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }

      // If in fullscreen and playing, hide controls
      if (this.isFullscreen && this.isPlaying) {
        this.showControls = false;
        this.forceHideAllUI = true;
      } else {
        // Otherwise show controls temporarily
        this.showControlsTemporarily();
      }
    },

    enterFullscreen() {
      const element = this.$el;

      try {
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        }

        this.isFullscreen = true;
        document.body.classList.add('has-fullscreen-element');

        // Lock orientation to landscape if possible
        if (screen.orientation && screen.orientation.lock) {
          screen.orientation.lock('landscape').catch(e => {
            console.log('Orientation lock not supported');
          });
        }

        // Add event listeners for resizing
        window.addEventListener('resize', this.handleFullscreenResize);
        window.addEventListener('orientationchange', this.handleFullscreenResize);

        // Force immediate resize handling
        this.handleFullscreenResize();
      } catch (e) {
        console.error('Fullscreen API error:', e);
        this.isFullscreen = true;
        document.body.classList.add('has-fullscreen-element');
      }
    },

    exitFullscreen() {
      try {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }

        // Unlock orientation
        if (screen.orientation && screen.orientation.unlock) {
          screen.orientation.unlock();
        }

        // Remove event listeners
        window.removeEventListener('resize', this.handleFullscreenResize);
        window.removeEventListener('orientationchange', this.handleFullscreenResize);

        document.body.classList.remove('has-fullscreen-element');

        // Clean up styles
        this.$el.style.transform = '';
        this.$el.style.position = '';
        this.$el.style.top = '';
        this.$el.style.marginTop = '';

        // Emit event
        this.$emit('fullscreen-exited');
      } catch (e) {
        console.error('Exit fullscreen error:', e);
      }

      this.isFullscreen = false;
    },

    handleFullscreenResize() {
      if (this.isFullscreen) {
        this.$nextTick(() => {
          const element = this.$el;
          if (element) {
            // Force reflow for mobile browsers
            element.style.display = 'none';
            const forceRepaint = element.offsetHeight;
            element.style.display = '';

            setTimeout(() => {
              // Force hardware acceleration
              element.style.transform = 'translateZ(0)';

              // Ensure content fills the screen
              const viewportHeight = window.innerHeight;
              if (element.offsetHeight < viewportHeight) {
                element.style.height = `${viewportHeight}px`;
              }
            }, 50);
          }
        });
      }
    },

    handleFullscreenChange() {
      const isInFullscreen = !!(document.fullscreenElement ||
                                document.webkitFullscreenElement ||
                                document.msFullscreenElement);

      this.isFullscreen = isInFullscreen;

      if (isInFullscreen) {
        document.body.classList.add('has-fullscreen-element');
      } else {
        document.body.classList.remove('has-fullscreen-element');
      }
    }
  }
};
</script>

<style scoped>
/* Recording toast notification - outside the recording area */
.recording-toast {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10001;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.recording-toast-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.recording-dot {
  width: 8px;
  height: 8px;
  background: #dc2626;
  border-radius: 50%;
  animation: recording-pulse 1.5s ease-in-out infinite;
}

@keyframes recording-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.3);
  }
}

.recording-toast kbd {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 2px 6px;
  font-family: monospace;
  font-size: 13px;
  margin: 0 3px;
  display: inline-block;
  line-height: 1;
  vertical-align: middle;
}

/* Recording error notification */
.recording-error-notification {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(220, 38, 38, 0.9);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10001;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.error-close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  margin-left: 8px;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.error-close-btn:hover {
  opacity: 1;
}

/* Recording-specific styles */
.segment-image-panel.is-recording .ui-controls-layer,
.empty-state-placeholder.is-recording .ui-controls-layer {
  pointer-events: none;
}

.segment-image-panel.is-recording .ui-controls-layer.recording-hidden,
.empty-state-placeholder.is-recording .ui-controls-layer.recording-hidden {
  opacity: 0;
  visibility: hidden;
}

/* Force hide all UI during recording */
.segment-image-panel.is-recording .ui-controls-layer.hidden,
.empty-state-placeholder.is-recording .ui-controls-layer.hidden {
  opacity: 0 !important;
  visibility: hidden !important;
}

.segment-image-panel.is-recording .ui-controls-layer.hidden *,
.empty-state-placeholder.is-recording .ui-controls-layer.hidden * {
  opacity: 0 !important;
  visibility: hidden !important;
  display: none !important;
}

/* Ensure transcript remains visible during recording */
.segment-image-panel.is-recording .live-transcript,
.empty-state-placeholder.is-recording .live-transcript {
  visibility: visible !important;
  opacity: 1 !important;
}

/* Large record button */
.large-record-button {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border: 3px solid rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.4);
  transition: all 0.3s ease;
  margin: 0 1rem;
  position: relative;
  overflow: hidden;
}

.large-record-button:before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%);
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.large-record-button:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.5);
}

.large-record-button:hover:before {
  opacity: 1;
}

.large-record-button svg {
  z-index: 1;
  width: 36px;
  height: 36px;
}

.large-record-button svg circle:last-child {
  fill: currentColor;
  animation: pulse-record 2s ease-in-out infinite;
}

@keyframes pulse-record {
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

/* Recording mode toggle */
.settings-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 8px;
}

.settings-hint {
  opacity: 0.7;
  font-size: 0.85em;
  font-weight: normal;
  margin-left: 8px;
}

/* In recording mode, make the fullscreen toggle more discreet */
.fullscreen-toggle.recording-mode {
  width: 36px;
  height: 36px;
  opacity: 0.6;
  background: rgba(0, 0, 0, 0.5);
}

/* Focus styles */
.segment-image-panel:focus,
.empty-state-placeholder:focus {
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
  outline: none;
}

/* =================== Original styles =================== */
.segment-image-panel {
  background-color: rgba(59, 130, 246, 0.1);
  border-radius: 8px;
  padding: 1px 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.image-display {
  cursor: pointer;
  padding: 0;
  position: relative;
}

.image-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.05);
  position: relative;
}

.image-animation-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.document-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.placeholder-image {
  width: 100%;
  max-width: 1200px;
  height: auto;
  border-radius: 8px;
  opacity: 0.7;
  margin-bottom: 16px;
}

.image-container img.fallback-image {
  object-fit: contain;  /* Good - prevents scrolling */
  opacity: 0.7;
  max-width: 100%;      /* Add this */
  max-height: 100%;     /* Add this */
  width: auto;          /* Add this */
  height: auto;         /* Add this */
}

.image-container:hover img {
  transform: scale(1.02);
}

.image-footer {
  margin-top: 12px;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: 0 0 12px 12px;
}

.image-title {
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 4px;
}

.image-source {
  font-size: 12px;
  opacity: 0.8;
}

.image-source a {
  color: white;
  text-decoration: none;
  transition: opacity 0.2s;
}

.image-source a:hover {
  opacity: 1;
  text-decoration: underline;
}

.image-error-message {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  background: rgba(0, 0, 0, 0.7);
  padding: 8px;
  color: white;
  font-size: 14px;
}

/* Empty state styling */
.empty-state-placeholder {
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  padding: 0px 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: all 0.3s ease;
  animation: fadeSlideIn 0.3s ease-out;
  position: relative;
}

.placeholder-image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.placeholder-image-container .image-container {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
}

/* UI Controls Layer */
.ui-controls-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  pointer-events: none; /* Let clicks pass through by default */
  transition: opacity 0.5s ease;
}

/* Hide controls while playing */
.ui-controls-layer.hidden {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.5s ease, visibility 0s 0.5s;
}

/* Make buttons clickable */
.ui-controls-layer button {
  pointer-events: auto;
}

/* Fullscreen toggle button */
.fullscreen-toggle {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(28, 28, 28, 0.7);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  opacity: 0.7;
  z-index: 15;
}

.fullscreen-toggle:hover {
  background: rgba(60, 60, 60, 0.9);
  transform: scale(1.05);
  opacity: 1;
}

/* Large play button and navigation */
.large-play-button-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
}

.large-play-button {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(45, 29, 74, 0.8);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, background-color 0.2s ease;
  margin: 0 1rem;
}

.large-play-button:hover {
  transform: scale(1.1);
  background: rgba(66, 153, 225, 0.9);
}

.large-play-button svg {
  margin-left: 4px;
}

.nav-button {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(45, 29, 74, 0.8);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.nav-button:hover {
  transform: scale(1.1);
  background: rgba(66, 153, 225, 0.9);
}

/* Settings toggle and panel */
.recording-settings {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 15;
}

.settings-toggle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(28, 28, 28, 0.7);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.settings-toggle:hover {
  background: rgba(60, 60, 60, 0.9);
  transform: scale(1.05);
}

.settings-panel {
  background: rgba(28, 28, 28, 0.85);
  border-radius: 8px;
  padding: 16px;
  width: 320px;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
  margin-top: 8px;
}

.settings-section {
  margin-bottom: 16px;
}

.settings-section:last-child {
  margin-bottom: 0;
}

.settings-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.setting-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.setting-button {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 6px 10px;
  font-size: 13px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.setting-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.setting-button.active {
  background: rgba(66, 153, 225, 0.6);
}

/* Animation options */
.animation-options {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.settings-checkbox input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
}

/* Keyboard shortcuts overlay */
.keyboard-shortcuts-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  pointer-events: auto;
}

.keyboard-shortcuts-panel {
  background: rgba(40, 40, 40, 0.95);
  border-radius: 8px;
  padding: 24px;
  width: 360px;
  max-width: 90vw;
  color: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.keyboard-shortcuts-panel h3 {
  text-align: center;
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 18px;
}

.shortcut-list {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px 16px;
  margin-bottom: 20px;
}

.shortcut-item {
  display: contents;
}

.key {
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  padding: 4px 8px;
  min-width: 24px;
  text-align: center;
  font-family: monospace;
  font-weight: bold;
}

.close-button {
  display: block;
  margin: 0 auto;
  background: rgba(66, 153, 225, 0.6);
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.close-button:hover {
  background: rgba(66, 153, 225, 0.8);
}

/* Animation transition helper */
.animation-transition {
  transition: transform 1s ease-in-out, opacity 1s ease-in-out;
}

/* Non-repeating animations */
.image-animation-container.no-repeat {
  animation-iteration-count: 1 !important;
  animation-fill-mode: forwards !important;
}

/* Animation keyframes and styles */
.zoom-pan-effect {
  animation: zoomPan 30s ease-in-out infinite;
}

.hollywood-dramatic {
  animation: hollywoodDramatic 30s ease-in-out infinite;
}

.ken-burns-effect {
  animation: kenBurns 40s ease-in-out infinite;
}

.fade-and-scale {
  animation: fadeScale 30s ease-in-out infinite;
}

.gentle-float {
  animation: gentleFloat 25s ease-in-out infinite;
}

.smooth-slide {
  animation: smoothSlide 35s ease-in-out infinite;
}

/* Modified keyframe animations for FullscreenImagePanel */
@keyframes zoomPan {
  0% {
    transform: scale(1) translate(0, 0);
  }
  25% {
    transform: scale(1.05) translate(-1%, -0.5%);
  }
  50%, 100% {
    transform: scale(1.1) translate(-2%, -1%);
  }
}

@keyframes kenBurns {
  0% {
    transform: scale(1.05) translate(-1%, -1%);
  }
  25% {
    transform: scale(1.09) translate(0%, 1%);
  }
  50%, 100% {
    transform: scale(1.12) translate(1%, 1%);
  }
}

@keyframes fadeScale {
  0% {
    opacity: 0.95;
    transform: scale(1);
  }
  50%, 100% {
    opacity: 1;
    transform: scale(1.08);
  }
}

@keyframes gentleFloat {
  0% {
    transform: translateY(0) scale(1.02);
  }
  50%, 100% {
    transform: translateY(-10px) scale(1.05);
  }
}

@keyframes smoothSlide {
  0% {
    transform: translateX(-2%) scale(1.05);
  }
  50%, 100% {
    transform: translateX(2%) scale(1.08);
  }
}

@keyframes hollywoodDramatic {
  0% {
    transform: scale(1) translate(0, 0) rotate(0deg);
    filter: brightness(0.9) contrast(0.9);
  }
  15% {
    transform: scale(1.08) translate(-1%, -1%) rotate(-0.5deg);
    filter: brightness(0.95) contrast(1.05);
  }
  35% {
    transform: scale(1.15) translate(2%, -2%) rotate(0.3deg);
    filter: brightness(1.05) contrast(1.15);
  }
  65% {
    transform: scale(1.25) translate(-3%, -1%) rotate(-0.2deg);
    filter: brightness(1.1) contrast(1.2);
  }
  85%, 100% {
    transform: scale(1.35) translate(1%, -3%) rotate(0.1deg);
    filter: brightness(1.15) contrast(1.25);
  }
}

/* Mobile optimization */
@media (max-width: 768px) {
  .segment-image-panel, .empty-state-placeholder {
    padding: 12px;
    margin-bottom: 12px;
  }

  .image-display {
    padding: 0;
  }

  .image-container {
    max-height: calc(100vh - 150px);
  }

  .fullscreen-toggle, .settings-toggle {
    width: 36px;
    height: 36px;
  }

  .large-play-button {
    width: 60px;
    height: 60px;
  }

  .large-play-button svg {
    width: 36px;
    height: 36px;
  }

  .nav-button {
    width: 40px;
    height: 40px;
  }

  .settings-panel {
    width: 280px;
    padding: 12px;
  }

  .placeholder-image {
    max-width: 100%;
  }

  .large-record-button {
    width: 60px;
    height: 60px;
  }

  .large-record-button svg {
    width: 32px;
    height: 32px;
  }
}

/* Class for fullscreen state */
.segment-image-panel.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  padding: 0 !important;
  border-radius: 0;
  z-index: 9999;
  background-color: black;
  transition: none;
  overflow: hidden;
}

.empty-state-placeholder.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  padding: 0 !important;
  border-radius: 0;
  z-index: 9999;
  background-color: black;
  transition: none;
  overflow: hidden;
}

.is-fullscreen .image-container {
  max-height: none;
  height: 100vh;
  border-radius: 0;
}

.is-fullscreen .placeholder-image-container {
  width: 100%;
  height: 100%;
}

.is-fullscreen .document-image {
  max-height: 100vh;
}

.is-fullscreen .image-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 0;
}

/* iOS Safari fixes for fullscreen */
@supports (-webkit-touch-callout: none) {
  .segment-image-panel.is-fullscreen,
  .empty-state-placeholder.is-fullscreen {
    top: env(safe-area-inset-top, 0);
    bottom: env(safe-area-inset-bottom, 0);
    left: env(safe-area-inset-left, 0);
    right: env(safe-area-inset-right, 0);
    height: -webkit-fill-available;
  }
}

/* Aspect ratio classes */
.aspect-16-9 .image-container {
  aspect-ratio: 16/9;
}

.aspect-4-3 .image-container {
  aspect-ratio: 4/3;
}

.aspect-1-1 .image-container {
  aspect-ratio: 1/1;
}

.aspect-9-16 .image-container {
  aspect-ratio: 9/16;
}

.is-fullscreen .image-container {
  aspect-ratio: unset;
}

/* Screen capture optimizations - prevent flickering in recordings */
.segment-image-panel.is-fullscreen,
.empty-state-placeholder.is-fullscreen {
  backface-visibility: hidden; /* Prevent flickering */
  transform: translateZ(0); /* Force hardware acceleration */
  will-change: transform; /* Hint to browser for optimization */
}

/* Ensure smooth animation playback in fullscreen */
.segment-image-panel.is-fullscreen .image-animation-container,
.empty-state-placeholder.is-fullscreen .image-animation-container {
  backface-visibility: hidden;
  transform: translateZ(0);
  will-change: transform;
}

/* Fade transition animation */
@keyframes fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Add or modify this CSS rule */
.is-fullscreen.is-playing .ui-controls-layer {
  opacity: 0 !important;
  visibility: hidden !important;
  pointer-events: none !important;
}
</style>
